import { useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  assignStoresToPointPromotionApi,
  deletePointPromotionStoreApi,
  getPointPromotionStoresApi,
} from "../api";
import { getStoreList } from "../../Stores/api/StoreApi";

export const usePointPromotionStores = ({ promotionId, promotionData }) => {
  const [state, setState] = useImmer({
    isBusy: false,
    isDelete: false,
    isOpen: false,
    isApplyAllConfirmOpen: false,
    storeId: null,
    assign: {
      storeList: [],
      allStores: [],
      filteredStores: [],
      selectedStores: [],
      isModalLoading: false,
      isSaveButtonBusy: false,
    },
  });

  const getPointPromotionStores = async () => {
    if (!promotionId) {
      setState((draft) => {
        draft.assign.storeList = Array.isArray(promotionData?.stores)
          ? promotionData.stores
          : [];
      });
      return;
    }

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await getPointPromotionStoresApi(promotionId);
      if (res?.success) {
        setState((draft) => {
          draft.assign.storeList = res.data || [];
          draft.assign.selectedStores = (res.data || []).map((item) => item.id);
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load stores");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const getAvailableStores = async () => {
    try {
      setState((draft) => {
        draft.assign.isModalLoading = true;
      });
      const res = await getStoreList();

      if (res?.success) {
        const stores = Array.isArray(res.data) ? res.data : [];
        setState((draft) => {
          draft.assign.allStores = stores;
          draft.assign.filteredStores = stores;
          draft.assign.isModalLoading = false;
        });
      } else {
        setState((draft) => {
          draft.assign.isModalLoading = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to load stores",
      );
      setState((draft) => {
        draft.assign.isModalLoading = false;
      });
    }
  };

  useEffect(() => {
    getPointPromotionStores();
  }, [promotionId, promotionData]);

  useEffect(() => {
    if (promotionId) {
      getAvailableStores();
    }
  }, [promotionId]);

  const openAssignModal = () => {
    setState((draft) => {
      draft.isOpen = true;
      draft.assign.filteredStores = draft.assign.allStores;
      draft.assign.selectedStores = (draft.assign.storeList || []).map(
        (item) => item.id,
      );
    });
  };

  const closeAssignModal = () => {
    setState((draft) => {
      draft.isOpen = false;
    });
  };

  const HandleFilterStore = (text) => {
    const storeList = state.assign.allStores;

    if (!text) {
      setState((draft) => {
        draft.assign.filteredStores = storeList;
      });
      return;
    }

    const normalized = text.toLowerCase();
    const filtered = storeList.filter((store) =>
      store.name.toString().toLowerCase().includes(normalized),
    );

    setState((draft) => {
      draft.assign.filteredStores = filtered;
    });
  };

  const handleStoreSelect = (storeId, checked) => {
    if (checked) {
      setState((draft) => {
        draft.assign.selectedStores = [...draft.assign.selectedStores, storeId];
      });
    } else {
      setState((draft) => {
        draft.assign.selectedStores = draft.assign.selectedStores.filter(
          (id) => id !== storeId,
        );
      });
    }
  };

  const handleSelectAllStores = (checked) => {
    if (checked) {
      setState((draft) => {
        draft.assign.selectedStores = state.assign.filteredStores.map((store) => store.id);
      });
    } else {
      setState((draft) => {
        draft.assign.selectedStores = [];
      });
    }
  };

  const handleAssignStores = async () => {
    const payload = {
      store_id: state.assign.selectedStores,
    };

    if (!promotionId) return;

    setState((draft) => {
      draft.assign.isSaveButtonBusy = true;
    });

    try {
      const res = await assignStoresToPointPromotionApi(promotionId, payload);
      if (res?.success) {
        successMessage(res.message || "Stores assigned successfully");
        setState((draft) => {
          draft.assign.isSaveButtonBusy = false;
          draft.isOpen = false;
        });
        getPointPromotionStores();
      } else {
        setState((draft) => {
          draft.assign.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to assign stores");
      setState((draft) => {
        draft.assign.isSaveButtonBusy = false;
      });
    }
  };

  const DeleteModals = (open, id = null) => {
    setState((draft) => {
      draft.isDelete = open;
      draft.storeId = id;
    });
  };

  const handleDeleteStore = async () => {
    if (!promotionId || !state.storeId) return;

    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await deletePointPromotionStoreApi(promotionId, state.storeId);
      if (res?.success) {
        successMessage(res.message || "Store removed successfully");
        setState((draft) => {
          draft.assign.storeList = draft.assign.storeList.filter(
            (item) => item.id !== state.storeId,
          );
          draft.assign.selectedStores = draft.assign.selectedStores.filter(
            (id) => id !== state.storeId,
          );
          draft.isDelete = false;
          draft.storeId = null;
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to delete store");
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const applyToAllStores = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const res = await assignStoresToPointPromotionApi(promotionId, {
        select_all: true,
      });

      if (res?.success) {
        successMessage(res.message || "Applied to all stores successfully");
        handleApplyAllConfirmModal(false);
        await getPointPromotionStores();
        setState((draft) => {
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to apply to all stores",
      );
      setState((draft) => {
        draft.isBusy = false;
      });
    }
  };

  const handleApplyAllConfirmModal = (isOpen) => {
    setState((draft) => {
      draft.isApplyAllConfirmOpen = isOpen;
    });
  };

  return {
    state,
    openAssignModal,
    closeAssignModal,
    HandleFilterStore,
    handleStoreSelect,
    handleAssignStores,
    handleSelectAllStores,
    DeleteModals,
    handleDeleteStore,
    applyToAllStores,
    handleApplyAllConfirmModal,
  };
};
